import { useQuery } from '@tanstack/react-query'
import type { RosterAdminApi } from '../adminApi.js'
import type { BehaviorsAdminApi } from '../../behaviors/index.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'
import type { MessagesAdminApi } from '../../messages/index.js'
import type { PartnerAdminApi } from '../../partner/index.js'
import type { ScheduleAdminApi } from '../../schedule/index.js'
import type { ScoringAdminApi } from '../../scoring/index.js'
import type { WorkerDetailHooks } from '../components/workerDetailHooks.js'

/** The admin calls this screen uses. */
type AdminApi = RosterAdminApi & FacilitiesAdminApi & ScoringAdminApi & ScheduleAdminApi & MessagesAdminApi & PartnerAdminApi & BehaviorsAdminApi

/**
 * The worker scoring panel's reads through the platform-admin routes for
 * one client, cached under the admin's own keys. Built once per client so
 * the panel calls the same hooks in the same order on every render.
 */
export function createAdminWorkerDetailHooks(api: AdminApi, clientId: string): WorkerDetailHooks {
  const key = (workerId: string | null, part: string, extra: string | null = null) => ['admin', 'worker', clientId, workerId, part, ...(extra === null ? [] : [extra])]
  /** A worker-scoped read, bound to the worker once the row names one. */
  const of =
    <T,>(read: (clientId: string, workerId: string) => Promise<T>) =>
    (workerId: string | null) =>
    () =>
      read(clientId, workerId ?? '')

  return {
    useProfile: (workerId) => useQuery({ queryKey: key(workerId, 'profile'), queryFn: of(api.workerProfile)(workerId), enabled: workerId !== null }),
    useScoreBreakdown: (workerId) => useQuery({ queryKey: key(workerId, 'breakdown'), queryFn: of(api.workerScoreBreakdown)(workerId), enabled: workerId !== null }),
    useScore: (workerId) => useQuery({ queryKey: key(workerId, 'score'), queryFn: of(api.workerScore)(workerId), enabled: workerId !== null }),
    useScoreForPeriod: (workerId, period) =>
      useQuery({ queryKey: key(workerId, 'score', period), queryFn: () => api.workerScoreForPeriod(clientId, workerId ?? '', period ?? ''), enabled: workerId !== null && period !== null }),
    useScoreHistory: (workerId) => useQuery({ queryKey: key(workerId, 'score-history'), queryFn: of(api.workerScoreHistory)(workerId), enabled: workerId !== null }),
    useBidWindow: (workerId) => useQuery({ queryKey: key(workerId, 'bid-window'), queryFn: of(api.workerBidWindow)(workerId), enabled: workerId !== null }),
    useNotifications: (workerId) => useQuery({ queryKey: key(workerId, 'notifications'), queryFn: of(api.workerNotifications)(workerId), enabled: workerId !== null }),
    useOutcomes: (workerId) => useQuery({ queryKey: key(workerId, 'outcomes'), queryFn: of(api.workerOutcomes)(workerId), enabled: workerId !== null }),
    useBehaviorEvents: (workerId) => useQuery({ queryKey: key(workerId, 'behavior-events'), queryFn: of(api.workerBehaviorEvents)(workerId), enabled: workerId !== null }),
    useBarriers: (workerId) => useQuery({ queryKey: key(workerId, 'barriers'), queryFn: of(api.workerBarriers)(workerId), enabled: workerId !== null }),
    useFacilityConfig: (facilityId) =>
      useQuery({ queryKey: ['admin', 'facility', clientId, facilityId, 'config'], queryFn: () => api.facilityConfig(clientId, facilityId ?? ''), enabled: facilityId !== null }),
  }
}
